import axios from 'axios';

// This client carries patient medical details, passport data and contact
// information. Nothing about a request or response body may be logged in a
// production build - see `debugLog` below.
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Development-only logger.
 *
 * Previously this file logged every request URL and the full JSON payload of
 * every POST/PATCH/PUT unconditionally, which wrote medical and passport data
 * into the browser console of the live site (and into anything scraping the
 * console, such as session-replay tooling). Logging is now compiled out of
 * production builds: `process.env.NODE_ENV` is inlined at build time, so the
 * bundler drops these branches entirely.
 */
const debugLog = (...args) => {
    if (isDevelopment) {
        console.log(...args);
    }
};

const getBaseUrl = () => {
    // Priority: .env > production URL
    let url = (process.env.REACT_APP_API_URL || 'https://api.bharatheal.com/api/v1').trim();

    // 1. Remove ALL trailing slashes
    url = url.replace(/\/+$/, '');

    // 2. FORCE inclusion of api/v1 if missing for the bharatheal.com domain
    const targetDomain = 'api.bharatheal.com';
    if (url.includes(targetDomain) && !url.includes('/api/v1')) {
        url += '/api/v1';
    }

    // 3. Final normalization: BASE_URL should NOT end with a slash
    return url.replace(/\/+$/, '');
};

const BASE_URL = getBaseUrl();

debugLog('[API Client] Final BASE_URL being used:', BASE_URL);

if (BASE_URL.includes('localhost') && !isDevelopment) {
    console.warn('[API Client] WARNING: Using localhost API URL in non-development environment!');
}

const apiClient = axios.create({
    baseURL: BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Interceptor to add auth token
apiClient.interceptors.request.use(
    (config) => {
        const urlPart = config.url?.startsWith('/') ? config.url : `/${config.url}`;

        // Block requests with "undefined" string in URL (common bug during navigation)
        if (urlPart.includes('/undefined')) {
            console.error(`[API Client] Blocked request with undefined parameter: ${urlPart}`);
            return Promise.reject(new Error(`Blocked request with undefined parameter: ${urlPart}`));
        }

        debugLog(`[API Request] ${config.method?.toUpperCase()} ${config.baseURL}${urlPart}`);

        // FormData must not carry an explicit Content-Type, or the browser
        // cannot append the multipart boundary. This is behaviour, not
        // logging, so it stays outside the debug guard.
        if (config.data instanceof FormData) {
            if (config.headers['Content-Type'] || config.headers['content-type']) {
                delete config.headers['Content-Type'];
                delete config.headers['content-type'];
            }
            debugLog('[API Request Payload] FormData with fields:', Array.from(config.data.keys()));
        } else if (['POST', 'PATCH', 'PUT'].includes(config.method?.toUpperCase()) && config.data) {
            // Payload bodies are dev-only: they contain patient PII.
            debugLog('[API Request Payload]', config.data);
        }

        const token = localStorage.getItem('access_token');
        const isAuthRoute = config.url && (config.url.includes('/auth/login') || config.url.includes('/auth/register'));

        if (token && token !== 'undefined' && !isAuthRoute) {
            config.headers = config.headers || {};
            config.headers['Authorization'] = `Bearer ${token}`;
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// Interceptor to handle common errors
apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        const url = originalRequest?.url || '';
        const isLoginPage = window.location.pathname.includes('/login');
        const isAuthRoute = url.includes('/auth/login') || url.includes('/auth/register');

        // Status and URL are safe to surface in production; response bodies are
        // not, because validation errors echo back the submitted field values.
        if (error.response) {
            console.error(`[API Error] ${error.response.status} ${error.response.statusText} ${url}`);
            debugLog('[API Error] Response Data:', error.response.data);
        } else if (error.code === 'ERR_NETWORK') {
            console.error('[API Client] Network or CORS error - check the API server is reachable.');
        } else {
            console.error('[API Error] Request failed:', error.message);
        }

        // Handle 401 - Token expired
        if (error.response?.status === 401 && !originalRequest?._retry) {
            // If we are already on the login page OR this was a login request,
            // do NOT redirect to login (avoids an infinite refresh loop).
            if (isLoginPage || isAuthRoute) {
                return Promise.reject(error);
            }

            originalRequest._retry = true;
            localStorage.removeItem('access_token');
            localStorage.removeItem('user_role');

            // Send admins back to the admin login rather than the customer one.
            const isAdminArea = window.location.pathname.startsWith('/admin');
            window.location.href = isAdminArea ? '/admin/login' : '/customer/login';
            return Promise.reject(error);
        }

        return Promise.reject(error);
    }
);

export default apiClient;
